import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { AuthService } from '../../auth/services/auth.service';
import { Roles } from '../models/roles';

/**
 * Role guard that checks if user has all required roles to access a route.
 *
 * Usage in routes:
 * ```
 * {
 *   path: 'admin',
 *   component: AdminComponent,
 *   canActivate: [roleGuard],
 *   data: { requiredRoles: ['ADMIN', 'MANAGER'] }
 * }
 * ```
 *
 * If requiredRoles is not present or empty, access is allowed.
 * If requiredRoles is present, user must have ALL specified roles.
 */
export const roleGuard: CanActivateFn = (route, state) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const requiredRoles: string[] = route.data?.['requiredRoles'];
  const requiredRolesMode: string = route.data?.['requiredRolesMode'] || 'all';

  if (!requiredRoles || requiredRoles.length === 0) {
    return true;
  }

  if (!authService.isLoggedIn()) {
    return false;
  }

  if (requiredRolesMode === 'all') {
    const hasAllRequiredRoles = requiredRoles.every((requiredRole) => authService.hasRole(requiredRole as Roles));

    if (!hasAllRequiredRoles) {
      router.navigate(['/nao-autorizado']);
      return false;
    }
  }

  const hasAnyRequiredRoles = requiredRoles.some((requiredRole) => authService.hasRole(requiredRole as Roles));

  if (!hasAnyRequiredRoles) {
    router.navigate(['/nao-autorizado']);
    return false;
  }

  return true;
};
